import React, { FC, useEffect, useState } from 'react';
import getBoatAvailability from '../utils/getBoatAvailability';
import format from 'date-fns/format';
import { nl, de, enGB } from 'date-fns/locale';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import DatepickerField from '@oberoninternal/travelbase-website/dist/components/DatepickerField';
import SelectInput from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import Formed from '@oberoninternal/travelbase-website/dist/components/Formed';
import { FormattedMessage } from 'react-intl';
import getHourAndMinutes from '../utils/getHourAndMinutes';
import Caret from './icons/Caret';

interface AvailabilityProp {
    departureTime: string;
    vehicleName: string;
    remainingPersonCapacity: number;
    remainingVehicleCapacity: number;
}

export interface PortProp {
    value: string;
    label: string;
    portCodeFrom: string;
    portCodeTo: string;
}

const ports: PortProp[] = [
    {
        value: 'Harlingen - Terschelling',
        label: 'Harlingen - Terschelling',
        portCodeFrom: 'H',
        portCodeTo: 'T',
    },
    {
        value: 'Terschelling - Harlingen',
        label: 'Terschelling - Harlingen',
        portCodeFrom: 'T',
        portCodeTo: 'H',
    },
];

const AvailabilityTable: FC<React.PropsWithChildren<unknown>> = () => {
    const [availability, setAvailability] = useState<AvailabilityProp[]>([]);
    const router = useRouter();
    const { locale } = router;

    const availabilityDate = (date: string) => {
        let selectedLocale;

        switch (locale) {
            case 'de':
                selectedLocale = de;
                break;
            case 'en':
                selectedLocale = enGB;
                break;
            default:
                selectedLocale = nl;
        }

        const availableDate = format(new Date(date), 'dd MMM yyyy', { locale: selectedLocale }).toLocaleLowerCase();
        return availableDate;
    };

    const fetchPositions = async (dateValue: Date, port: PortProp) => {
        const hasDate = dateValue ? new Date(dateValue) : new Date();
        const response = await getBoatAvailability(hasDate, port);

        setAvailability(response);
    };

    useEffect(() => {
        fetchPositions(new Date(), ports[0]);
    }, []);

    return (
        <div>
            <HeadingTitle>
                <FormattedMessage defaultMessage="Actuele capaciteit op de boot" />
            </HeadingTitle>
            <FormWrapper>
                <Formed
                    initialValues={{
                        boat: new Date(),
                        boatAvailabillity: ports[0].value,
                    }}
                    handleAutoSave={values => {
                        fetchPositions(
                            values.boat,
                            ports.find(item => item.value === values.boatAvailabillity) ?? ports[0]
                        );
                    }}
                >
                    {() => (
                        <>
                            <StyledDatepickerField align="left" name="boat" />
                            <StyledSelectInputWrapper>
                                <SelectInput
                                    name="boatAvailabillity"
                                    hideChoices
                                    options={ports}
                                    ToggleComponent={Caret}
                                />
                            </StyledSelectInputWrapper>
                        </>
                    )}
                </Formed>
            </FormWrapper>
            <Table>
                {availability.map((item, key) => (
                    <TableRow key={key}>
                        <TableData>{getHourAndMinutes(item.departureTime!)}</TableData>
                        <TableData>{availabilityDate(item.departureTime!)}</TableData>
                        <TableData>{item.vehicleName}</TableData>
                        <TableData>
                            <TableDataNumber>{item.remainingPersonCapacity}</TableDataNumber>
                            <FormattedMessage defaultMessage="zitplaatsen" />
                            <Label>
                                <FormattedMessage defaultMessage="beschikbaar" />
                            </Label>
                        </TableData>
                        <TableData>
                            <TableDataNumber>{item.remainingVehicleCapacity}</TableDataNumber>
                            <FormattedMessage defaultMessage="autometer" />
                            <Label>
                                <FormattedMessage defaultMessage="beschikbaar" />
                            </Label>
                        </TableData>
                        <MobileOnly>
                            <MobileHeading>
                                <HeadinItem>{getHourAndMinutes(item.departureTime!)}</HeadinItem>
                                <HeadinItem>{availabilityDate(item.departureTime!)}</HeadinItem>
                                <HeadinItem>{item.vehicleName}</HeadinItem>
                            </MobileHeading>
                            <MobileBottom>
                                <BottomItem>
                                    <TableDataNumber>{item.remainingPersonCapacity}</TableDataNumber>
                                    <BottomLabel>
                                        <FormattedMessage defaultMessage="zitplaatsen" />
                                    </BottomLabel>
                                    <Label>
                                        <FormattedMessage defaultMessage="beschikbaar" />
                                    </Label>
                                </BottomItem>
                                <BottomItem>
                                    <TableDataNumber>{item.remainingVehicleCapacity}</TableDataNumber>
                                    <BottomLabel>
                                        <FormattedMessage defaultMessage="autometer" />
                                    </BottomLabel>
                                    <Label>
                                        <FormattedMessage defaultMessage="beschikbaar" />
                                    </Label>
                                </BottomItem>
                            </MobileBottom>
                        </MobileOnly>
                    </TableRow>
                ))}
            </Table>
        </div>
    );
};

const Table = styled.table`
    width: 100%;
    border-spacing: 0;
    margin-top: 4rem;
`;

const TableRow = styled.tr`
    background-color: ${({ theme }) => theme.colors.neutral[10]};
    :nth-of-type(even) {
        background-color: ${({ theme }) => theme.colors.neutral[0]};
    }
`;

const MobileOnly = styled.td`
    padding: 1.6rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: none;
    }
`;

const MobileHeading = styled.div`
    display: flex;
    color: ${({ theme }) => theme.colors.neutral[80]};
    font-weight: 500;
    font-size: 1.2rem;
`;

const HeadinItem = styled.div`
    margin-right: 2rem;
`;

const MobileBottom = styled.div`
    display: flex;
    margin-top: 0.4rem;
`;

const BottomItem = styled.div`
    margin-right: 2rem;
`;

const BottomLabel = styled.span`
    color: ${({ theme }) => theme.colors.neutral[80]};
    font-weight: 500;
    font-size: 1.2rem;
`;

const TableData = styled.td`
    padding: 2.4rem 2.7rem;
    font-weight: 600;
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${({ theme }) => theme.colors.neutral[80]};

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        display: none;
    }
`;
const TableDataNumber = styled.span`
    margin-right: 0.4rem;
    color: ${({ theme }) => theme.colors.positive[50]};

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        font-size: 1.2rem;
    }
`;

const Label = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.neutral[50]};
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
`;

const FormWrapper = styled.div`
    display: flex;
    width: 60%;
    justify-content: space-between;

    form {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin-top: 5rem;
        width: 100%;
    }
`;

const StyledSelectInputWrapper = styled.div`
    width: 60%;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 50%;
    }
`;

const StyledDatepickerField = styled(DatepickerField)`
    width: 30%;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 40%;
    }
`;

const HeadingTitle = styled.h1`
    margin-bottom: 3rem;
`;

export default AvailabilityTable;
